import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { DSSetup, Service } from 'webapp-types';
import { SortFieldType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockSettingsMetricComponent from 'webapp/components/block/settings/metric';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import NoRerenderSortable from 'webapp/models-custom/no-rerender-sortable';
import type DataSelectRecord from 'webapp/models/data-select';

interface Args {
	blockM: Block;
	isPanelOpen: boolean;
	onMetricOpen: () => void;
}

export default class BlockSettingsMetricsComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	// using store records bcos of possibly getting different models from same record
	@tracked openDataSelectRecords: DataSelectRecord[] = [];

	noRerenderSortable!: NoRerenderSortable<DataSelect>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.noRerenderSortable = new NoRerenderSortable<DataSelect>({
			elementSelector: '.bs__metric',
			dataIdProperty: 'id',
			dataSortProperty: 'order',
			getModel: () => {
				return this.model;
			},
			onNodeDelete: (el) => {
				el.remove();
			},
		});

		this.eventManagerService.on(AppEvent.BLOCK_CONTENT_CLICKED, this, (params) => {
			this.handleBlockContentEvent(params);
		});

		this.checkToAutoOpenFirstDataSelect(true);
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get model(): DataSelect[] {
		return this.blockSetup.dataSelectsShown;
	}

	get canAddMetric(): boolean {
		return (
			this.blockSetup.visualization.allowMultipleMetrics &&
			this.model.length < this.blockSetup.visualization.maxPrimaryMainDataSelectsAllowed
		);
	}

	get canSortMetrics(): boolean {
		return this.noRerenderSortable.renderedModel.length > 1;
	}

	get autoOpenFirstMetric() {
		const firstDs = this.model[0];
		return this.model.length === 1 && firstDs && !firstDs.metricSetup.key;
	}

	@action
	toggleDataSelect(ds: DataSelect): void {
		const isAlreadyOpen = this.openDataSelectRecords.includes(ds.model);
		if (this.args.isPanelOpen) {
			if (isAlreadyOpen) {
				this.closeDataSelect(ds);
			} else {
				this.openDataSelect(ds);
			}
		} else {
			if (!isAlreadyOpen) {
				// always open, dont toggle
				this.openDataSelect(ds);
			}
		}
	}

	@action
	addNewDataSelect(): void {
		void this.mmMetricsService.openMetricSelectPopup({
			onChange: (metricData: DSSetup.MetricData) => {
				const ds = this.blockSetup.createPrimaryMainDataSelect();
				const dsFromList = this.model.find((m) => m.model === ds.model);
				if (dsFromList) {
					this.openDataSelect(dsFromList);
				}
				this.blockSetup.changeMetric(ds, metricData);
				void this.blockSetup.afterSaves().then(() => {
					if (!ds.isNew) {
						this.eventManagerService.trigger(
							AppEvent.DESIGNER_UNIBLOCK_DATA_SELECT_ADDED,
							{
								dataSelect: ds,
							},
						);
					}
				});
			},
		});
	}

	@action
	handleBlockContentEvent(params: { dsId: number }) {
		// need a match among the current model
		const availableDs = this.model;
		let dsToOpen = availableDs.find((ds) => ds.numericId === params.dsId);
		if (!dsToOpen) {
			// maybe it was a secondary
			const dsOnBlock = this.blockSetup.dataSelects.find(
				(ds) => ds.numericId === params.dsId,
			);
			if (dsOnBlock?.updateFrom) {
				dsToOpen = availableDs.find((ds) => ds.numericId === dsOnBlock.updateFrom);
			}
		}
		if (dsToOpen) {
			this.openDataSelectRecords = []; // close all first
			this.openDataSelect(dsToOpen);
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.noRerenderSortable.ct = $(node);
		this.setupSortable(node);
	}

	@action
	teardownView(node: HTMLElement): void {
		this.destroySortable(node);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.BLOCK_CONTENT_CLICKED, this);
	}

	private openDataSelect(ds: DataSelect, skipUsingAddObject = false): void {
		if (skipUsingAddObject) {
			this.openDataSelectRecords = [ds.model];
		} else {
			if (!this.openDataSelectRecords.includes(ds.model)) {
				this.openDataSelectRecords = [...this.openDataSelectRecords, ds.model];
			}
		}
		this.args.onMetricOpen();
	}

	private closeDataSelect(ds: DataSelect): void {
		this.openDataSelectRecords = this.openDataSelectRecords.filter((dsRecord) => {
			return dsRecord !== ds.model;
		});
	}

	private cleanupOpenDataSelects() {
		// avoid filling the property with references to dataselects of previously selected blocks
		this.openDataSelectRecords = this.openDataSelectRecords.filter((dsRecord) => {
			return !!this.model.find((ds) => ds.model === dsRecord);
		});
	}

	private checkToAutoOpenFirstDataSelect(isInitialCheck = false) {
		if (this.autoOpenFirstMetric) {
			const firstDs = this.model[0];
			if (firstDs) {
				this.openDataSelect(firstDs, isInitialCheck);
			}
		}
	}

	// ------ START NO-RERENDER LOGIC -------

	@action
	onModelChange() {
		this.noRerenderSortable?.updateModel(false);
		this.cleanupOpenDataSelects();
		this.checkToAutoOpenFirstDataSelect();
	}

	@action
	checkModelOrder(): void {
		schedule('afterRender', this, this.noRerenderSortable.checkModelNodeOrder);
	}

	get dsOrder() {
		return this.model.map((item) => item.order);
	}

	// ------ END NO-RERENDER LOGIC	---------

	private getIndexes(el: JQuery<HTMLElement>): Record<string, number> {
		const indexes: Record<string, number> = {};
		el.find('>.bs__metric').each(function (index: number) {
			const id = $(this).data('id') as string | number; // a data select id
			indexes[id] = index;
		});
		return indexes;
	}

	private updateSortOrder(indexes: Record<string, number>): void {
		this.model.forEach((item) => {
			if (item.id) {
				const index = indexes[item.id];
				if (typeof index === 'number') {
					// won't be sent by BE
					item.directPropUpdate({ order: index });
				}
			}
		});

		// on metric order change, switch block sort to CUSTOM, if available
		const switchSortTo = SortFieldType.CUSTOM;
		const visualization = this.blockSetup.visualization;
		if (
			visualization.showSortOptions &&
			!!visualization.enabledSortOptions.find((o) => o.value === switchSortTo)
		) {
			this.blockSetup.changeSortField(switchSortTo);
		}
	}

	private setupSortable(node: HTMLElement): void {
		const el = $(node);

		el.sortable({
			items: '>.bs__metric',
			handle: '.bs__metric__title .sort-handle',
			axis: 'y',
			tolerance: 'pointer',
			placeholder: 'sort-placeholder',
			forcePlaceholderSize: true,
			start: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				ui.helper.addClass('sort-helper active-helper'); //for animation
			},
			beforeStop: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				ui.helper.removeClass('sort-helper active-helper');
			},
			update: (_event: JQuery.Event, ui: jQueryUIInstance) => {
				const id = ui.item.data('id') as string | number,
					indexes = this.getIndexes(el);

				this.updateSortOrder(indexes);

				// save the moved item
				const url = `/data-selects/${id}/order`;
				this.requestService
					.api('POST', url, { order: indexes[id] })
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}

	private destroySortable(node: HTMLElement): void {
		try {
			$(node).sortable('destroy');
		} catch (_e) {
			// skip
		}
	}
	<template>
		<div class="bs__metrics">

			<div
				{{didUpdate this.onModelChange this.model}}
				{{didUpdate this.checkModelOrder this.dsOrder}}
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
			>
				{{#each this.noRerenderSortable.renderedModel as |ds|}}
					<BlockSettingsMetricComponent
						@blockM={{@blockM}}
						@model={{ds}}
						@isSortable={{this.canSortMetrics}}
						@openRecords={{this.openDataSelectRecords}}
						@toggle={{fn this.toggleDataSelect ds}}
					/>
				{{/each}}
			</div>

			{{#if this.canAddMetric}}
				<a class="bs__metrics__add" {{on "click" this.addNewDataSelect}}>
					<CommonUiIconComponent @name="Plus" @size="md" />
					Add metric
				</a>
			{{/if}}

		</div>
	</template>
}
